import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

import { api } from '@/services/api-class';
import { hasValue } from '@/utils/legacy-utils';

// ============================================================================
// External Declarations
// ============================================================================

const callCardAndReportAuditLogEntryTemplate = (window as any).callCardAndReportAuditLogEntryTemplate;

// ============================================================================
// Types
// ============================================================================

interface CallCardAuditLogGridProps {
  callCardId?: number | null;
  callCardCreatedBeforeAuditLogging?: boolean;
  title?: string;
  active?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function CallCardAuditLogGrid({
  callCardId = null,
  callCardCreatedBeforeAuditLogging = false,
  title = 'Audit Log',
  active = false,
}: CallCardAuditLogGridProps) {
  const { theme } = useTheme();

  const [entries, setEntries] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoaded, setIsLoaded] = useState(false);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return (month < 10 ? '0' : '') + month + '/' +
      (day < 10 ? '0' : '') + day + '/' +
      year + ' ' +
      hours + ':' +
      (minutes < 10 ? '0' : '') + minutes + ':' +
      (seconds < 10 ? '0' : '') + seconds + ' ' + ampm;
  }, []);

  const getRole = useCallback((entry: any): string => {
    return entry.kerktonRole || entry.role || '';
  }, []);

  const formatEntry = useCallback((entry: string): string => {
    if (!entry) return '';
    if (typeof callCardAndReportAuditLogEntryTemplate === 'function') {
      return callCardAndReportAuditLogEntryTemplate(entry);
    }
    return entry;
  }, []);

  const getActionClass = useCallback((action: string): string => {
    switch (action) {
      case 'CREATE': return 'audit-action-create';
      case 'UPDATE': return 'audit-action-update';
      case 'STATUS_CHANGE': return 'audit-action-status';
      case 'DELETE': return 'audit-action-delete';
      case 'Viewed': return 'audit-action-view';
      default: return '';
    }
  }, []);

  const gridColumns = useMemo((): KstDataGridColumn[] => {
    return [
      {
        field: 'formattedDateTime', name: 'Date/Time', size: '160px',
        template: (e: any) => formatDateTime(e.dateCreated),
      },
      { field: 'loggedByFirstName', name: 'First', size: '100px' },
      { field: 'loggedByLastName', name: 'Last', size: '100px' },
      {
        field: 'role', name: 'Role', size: '100px',
        template: (e: any) => getRole(e),
      },
      {
        field: 'action', name: 'Action', size: '120px',
        template: (e: any) => `<span class="audit-action-badge ${getActionClass(e.action)}">${e.action}</span>`,
      },
      {
        field: 'entry', name: 'Entry',
        template: (e: any) => formatEntry(e.entry),
      },
    ];
  }, [formatDateTime, getRole, getActionClass, formatEntry]);

  const gridRows = useMemo((): KstDataGridRows => {
    return {
      entries: entries === null ? null : entries.map((e: any) => ({
        ...e,
        formattedDateTime: formatDateTime(e.dateCreated),
      })),
    };
  }, [entries, formatDateTime]);

  const gridPager = useMemo((): KstDataGridPager => {
    return {
      curPage: currentPage,
      numPages: totalPages,
      records: totalRecords,
      pageSize: pageSize,
      isVisible: totalPages > 1,
    };
  }, [currentPage, totalPages, totalRecords, pageSize]);

  const loadAuditLog = useCallback((page: number) => {
    if (!hasValue(callCardId)) {
      setEntries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const operation = {
      data: {
        page: page,
        take: pageSize,
        skip: (page - 1) * pageSize,
      },
    };

    api.getCallCardAuditLogEntries(
      callCardId,
      page,
      String(pageSize),
      JSON.stringify(operation),
      (result: any) => {
        setEntries(result.entries || []);
        setTotalRecords(result.records || 0);
        setTotalPages(Math.ceil((result.records || 0) / pageSize) || 1);
        setIsLoading(false);
        setIsLoaded(true);
      }
    );
  }, [callCardId, pageSize]);

  const goToPage = useCallback((page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    loadAuditLog(page);
  }, [totalPages, loadAuditLog]);

  const refresh = useCallback(() => {
    setCurrentPage(1);
    loadAuditLog(1);
  }, [loadAuditLog]);

  // Watch active prop - only load when tab becomes active and hasn't been loaded
  useEffect(() => {
    if (active && hasValue(callCardId) && !isLoaded) {
      setCurrentPage(1);
      loadAuditLog(1);
    }
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose refresh on window for external callers
  useEffect(() => {
    (window as any).CallCardAuditLogGridRefresh = refresh;
    return () => {
      if ((window as any).CallCardAuditLogGridRefresh === refresh) {
        (window as any).CallCardAuditLogGridRefresh = null;
      }
    };
  }, [refresh]);

  return (
    <div className={theme}>
      <KstDataGrid
        columns={gridColumns}
        rows={gridRows}
        pager={gridPager}
        loading={isLoading}
        showcheckbox={false}
        title={title}
        mobiletitlefield="formattedDateTime"
        onGoToPage={goToPage}
      />
      {callCardCreatedBeforeAuditLogging && (
        <div className="audit-log-warning-banner">
          <i className="fas fa-exclamation-triangle"></i>
          <span>
            <strong>Note:</strong> This Call for Service was created before audit logging. Not all changes may be logged.
          </span>
        </div>
      )}
    </div>
  );
}

(window as any).CallCardAuditLogGridReact = CallCardAuditLogGrid;
